import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { articles, newsTypes, timeRanges } from "@/mocks/articles";
import { usePageMeta } from "@/hooks/usePageMeta";
import { withinDays } from "@/utils/format";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterPanel, { type FilterGroup } from "@/components/feature/FilterPanel";
import Pagination from "@/components/feature/Pagination";

const PAGE_SIZE = 5;

const filterGroups: FilterGroup[] = [
  {
    key: "newsType",
    label: "新闻类别",
    options: newsTypes.map((type) => ({ label: type, value: type })),
  },
  {
    key: "time",
    label: "发布时间",
    options: timeRanges.map((range) => ({ label: range.label, value: range.value })),
  },
];

const newsArticles = articles.filter((item) => item.category === "news");

export default function NewsPage() {
  usePageMeta({
    title: "娱乐新闻频道 - 黑料吃瓜",
    description:
      "黑料吃瓜娱乐新闻频道发布最新娱乐资讯与行业动态，覆盖电影、电视剧、综艺与音乐演出市场，支持按时间与类别排序浏览。",
    keywords: "娱乐新闻,娱乐资讯,电影,电视剧,综艺,音乐,行业动态,黑料吃瓜",
  });

  const [filters, setFilters] = useState<Record<string, string>>({ newsType: "全部", time: "all" });
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () =>
      newsArticles
        .filter(
          (item) =>
            (filters.newsType === "全部" || item.newsType === filters.newsType) &&
            withinDays(item.date, filters.time),
        )
        .sort((a, b) => (order === "desc" ? (a.date < b.date ? 1 : -1) : a.date < b.date ? -1 : 1)),
    [filters, order],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleFilter = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const summary =
    [
      filters.newsType !== "全部" ? filters.newsType : "",
      filters.time !== "all" ? `近 ${filters.time} 天` : "",
    ]
      .filter(Boolean)
      .join(" · ") || "全部内容";

  return (
    <div className="max-w-[1320px] mx-auto px-4 md:px-6 py-5 md:py-7">
      <Breadcrumb items={[{ label: "娱乐新闻" }]} />

      <header className="mt-3 mb-5 md:mb-6">
        <h1 className="font-heading text-2xl md:text-[30px] font-black text-foreground-950">娱乐新闻频道</h1>
        <p className="text-[13px] md:text-sm text-foreground-500 leading-7 mt-2 max-w-3xl">
          行业动态、市场观察与项目进展。相比八卦与黑料，这里的内容更关注作品与产业本身：谁在投资、观众在看什么、市场正在发生什么变化。
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[256px_minmax(0,1fr)] gap-5 md:gap-6">
        <aside>
          <FilterPanel groups={filterGroups} values={filters} onChange={handleFilter} summary={summary} />
        </aside>

        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3.5">
            <p className="text-[13px] text-foreground-600">
              共 <span className="text-accent-600 font-bold">{filtered.length}</span> 条资讯
              <span className="hidden sm:inline text-foreground-400"> · 第 {currentPage} / {totalPages} 页</span>
            </p>
            <button
              type="button"
              onClick={() => {
                setOrder((prev) => (prev === "desc" ? "asc" : "desc"));
                setPage(1);
              }}
              className="inline-flex items-center gap-1.5 px-3.5 h-8 rounded-md border border-background-200 bg-background-50 text-[12.5px] text-foreground-700 whitespace-nowrap hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className="ri-sort-desc" />
              {order === "desc" ? "按时间：最新优先" : "按时间：最早优先"}
            </button>
          </div>

          {visible.length > 0 ? (
            <>
              <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                {visible.map((article, index) => (
                  <Link
                    key={article.id}
                    to={`/detail/${article.id}`}
                    className={`group flex gap-4 p-4 md:p-5 cursor-pointer hover:bg-background-100 transition-colors ${
                      index !== 0 ? "border-t border-background-200" : ""
                    }`}
                  >
                    <div className="w-24 h-20 md:w-36 md:h-24 shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img
                        src={article.cover}
                        alt={article.title}
                        title={`${article.title} - 黑料吃瓜`}
                        className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 text-[11.5px]">
                        <span className="px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 border border-primary-200">
                          {article.newsType}
                        </span>
                        <span className="text-foreground-400">{article.date}</span>
                        <span className="text-foreground-400 hidden sm:inline">· {article.author}</span>
                      </div>
                      <h2 className="font-heading text-[15.5px] md:text-[17px] font-bold text-foreground-900 leading-snug mt-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-[12.5px] md:text-[13px] text-foreground-500 leading-6 mt-1.5 line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
              <Pagination current={currentPage} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <i className="ri-newspaper-line text-3xl text-foreground-300" />
              <p className="font-heading text-base font-bold text-foreground-800 mt-3">该类别暂无新闻</p>
              <p className="text-[13px] text-foreground-500 mt-1.5">换一个类别或时间范围，会看到更多行业资讯。</p>
              <button
                type="button"
                onClick={() => {
                  setFilters({ newsType: "全部", time: "all" });
                  setPage(1);
                }}
                className="inline-flex items-center gap-1.5 mt-4 px-4 h-9 rounded-md bg-primary-500 text-background-50 text-[13px] whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}